import React, { useEffect } from 'react';
import { CheckCircle, XCircle, X } from 'lucide-react';

export interface ToastMessage {
  id: number;
  type: 'success' | 'error';
  message: string;
}

interface Props {
  toasts: ToastMessage[];
  onDismiss: (id: number) => void;
}

const ToastItem: React.FC<{ toast: ToastMessage; onDismiss: (id: number) => void }> = ({ toast, onDismiss }) => {
  useEffect(() => {
    const t = setTimeout(() => onDismiss(toast.id), 4000);
    return () => clearTimeout(t);
  }, [toast.id, onDismiss]);

  const isSuccess = toast.type === 'success';
  return (
    <div
      className={`animate-fade-in flex items-center gap-3 rounded-lg px-4 py-3 shadow-lg text-white ${
        isSuccess ? 'bg-green-600' : 'bg-red-600'
      }`}
    >
      {isSuccess ? <CheckCircle size={18} /> : <XCircle size={18} />}
      <span className="text-sm font-medium">{toast.message}</span>
      <button onClick={() => onDismiss(toast.id)} className="ml-2 opacity-80 hover:opacity-100">
        <X size={16} />
      </button>
    </div>
  );
};

const Toast: React.FC<Props> = ({ toasts, onDismiss }) => (
  <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
    {toasts.map(t => (
      <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
    ))}
  </div>
);

// Small hook so pages can push toasts without prop drilling.
export function useToasts() {
  const [toasts, setToasts] = React.useState<ToastMessage[]>([]);
  const push = (type: 'success' | 'error', message: string) =>
    setToasts(prev => [...prev, { id: Date.now() + Math.random(), type, message }]);
  const dismiss = (id: number) => setToasts(prev => prev.filter(t => t.id !== id));
  return {
    toasts,
    dismiss,
    success: (m: string) => push('success', m),
    error: (m: string) => push('error', m),
  };
}

export default Toast;
